import _ from "lodash";

import {
  DEFAULT_STANDARD_FILTERS,
  FEED_LIST_COLUMN_LIST,
  FILTERS,
  ARCHIVAL_STATE_OPTIONS,
  STANDARD_PLANNER_ELEMENT_TYPE_ENUM,
  DATA_SCOPE_ENUM,
} from "./Constants";
import {
  CURRICULUM_TYPE_PYP,
  GRADE_SCALE_TOOL,
} from "Constants/stringConstants";
import {
  getAcademicSetupType,
  getIsLearningCoursesEnabled,
  getIsSubjectBasedGradingEnabled,
} from "Teacher/routes/LearningCourses/modules/Utils";
import { getLearningCourseVariables } from "Teacher/routes/LearningCourse/modules/Utils";
import { getLearningCourseDetailsFromCache } from "Teacher/routes/LearningCourse/modules/LearningCourseGraphqlHelpers";
import { getAssessmentToolItem } from "Utils/AssessmentToolHelpers";
import { getDatasetsOrderByArgs } from "Datasets/modules/Utils";

export const getFeedListColumns = ({ canMapGradeScale = true, t } = {}) => {
  return _.reduce(
    FEED_LIST_COLUMN_LIST,
    (acc, list) => {
      if (!canMapGradeScale && list.key === "GRADE_SCALES") {
        return acc;
      }

      let style = list.style;
      if (!canMapGradeScale) {
        style = _.omit(style, ["backgroundColor"]);

        // When GRADE_SCALES column is hidden, let NAME stretch to fill the
        // remaining space so the table doesn't leave whitespace on the right.
        if (list.key === "NAME") {
          style = { ..._.omit(style, ["maxWidth"]), width: "100%" };
        }
      }

      acc.push({ ...list, value: t(list.value), style });
      return acc;
    },
    []
  );
};

export const getFilteredStandardSets = ({
  edges,
  assessmentTools,
  canMapGradeScale = true,
}) => {
  const gradeScaleEntityDetail = getAssessmentToolItem(
    assessmentTools,
    GRADE_SCALE_TOOL
  );

  const gradeScaleId = _.get(gradeScaleEntityDetail, "gradeScale.id");

  const mappedData = _.map(edges, ({ node, standardSet }) => {
    const { id, title, standardSetWrapper, isArchived = false } =
      node || standardSet || {};

    const gradeScales = _.filter(
      _.get(standardSetWrapper, "gradeScales", []),
      ({ id, isArchived = false }) => {
        return !isArchived && (!gradeScaleId || id === gradeScaleId);
      }
    );

    return {
      id,
      title,
      gradeScales,
      isArchived,
    };
  });

  if (!canMapGradeScale) {
    return _.filter(mappedData, item => !item?.isArchived);
  }

  return _.filter(
    mappedData,
    item => !item?.isArchived && !_.isEmpty(item?.gradeScales)
  );
};

export const hasNodesWithGradeScales = ({
  edges,
  assessmentTools,
  canMapGradeScale = true,
}) => {
  if (!canMapGradeScale) {
    return true;
  }

  const gradeScaleEntityDetail = getAssessmentToolItem(
    assessmentTools,
    GRADE_SCALE_TOOL
  );

  const gradeScaleId = _.get(gradeScaleEntityDetail, "gradeScale.id");

  return _.some(edges, ({ node, standardSet }) => {
    const { standardSetWrapper, isArchived = false } =
      node || standardSet || {};

    if (isArchived) {
      return false;
    }

    const gradeScales = _.filter(
      _.get(standardSetWrapper, "gradeScales", []),
      ({ id, isArchived = false }) => {
        return !isArchived && (!gradeScaleId || id === gradeScaleId);
      }
    );

    return !_.isEmpty(gradeScales);
  });
};

export const getLocalizedFilterLabels = t => ({
  grades: t("scopeAndSequence:grade_levels"),
  subjects: t("scopeAndSequence:subject_areas"),
  tags: t("common:tags"),
  archivalState: t("common:status"),
});

export const getAllFiltersCount = ({ currentFilters }) => {
  return _.reduce(
    currentFilters,
    (acc, filterValue, filterKey) => {
      if (_.isEqual(filterValue, DEFAULT_STANDARD_FILTERS[filterKey])) {
        return acc;
      } else {
        return acc + 1;
      }
    },
    0
  );
};

export const getAppliedFiltersCountForFilter = ({ filterKey, filterValue }) => {
  const defaultFilterValue = DEFAULT_STANDARD_FILTERS[filterKey];

  if (_.isEqual(filterValue, defaultFilterValue)) {
    return 0;
  }

  return _.isArray(filterValue) ? _.size(filterValue) : 1;
};

export const getFilterOverlayOptions = params => {
  const { t, grades, subjects, tags } = params;

  const localizedFilterLabels = getLocalizedFilterLabels(t);

  const academicSetupType = getAcademicSetupType();
  const filters = FILTERS[academicSetupType];

  return _.reduce(
    filters,
    (acc, filterKey) => {
      switch (filterKey) {
        case "grades": {
          if (!_.isEmpty(grades)) {
            acc.push({
              id: filterKey,
              label: localizedFilterLabels[filterKey],
              optionsType: "checkbox",
              filterOptions: _.map(grades, ({ id, name }) => ({
                id,
                label: name,
              })),
              includeAllOption: true,
              showSearch: true,
            });
          }

          return acc;
        }

        case "subjects": {
          if (!_.isEmpty(subjects)) {
            acc.push({
              id: filterKey,
              label: localizedFilterLabels[filterKey],
              optionsType: "checkbox",
              filterOptions: _.map(subjects, ({ id, name }) => ({
                id,
                label: name,
              })),
              includeAllOption: true,
              showSearch: true,
            });
          }

          return acc;
        }
        case "tags": {
          if (!_.isEmpty(tags)) {
            acc.push({
              id: filterKey,
              label: localizedFilterLabels[filterKey],
              optionsType: "checkbox",
              filterOptions: tags,
              includeAllOption: true,
              showSearch: true,
            });
          }

          return acc;
        }
        case "archivalState": {
          acc.push({
            id: filterKey,
            label: localizedFilterLabels[filterKey],
            optionsType: "radio",
            filterOptions: _.map(ARCHIVAL_STATE_OPTIONS, item => ({
              ...item,
              label: t(item.label),
            })),
          });

          return acc;
        }
        default:
          return acc;
      }
    },
    []
  );
};

export const getPublishedSubjectStandardQueryVariables = params => {
  const {
    id,
    searchText = "",
    standardFilters = DEFAULT_STANDARD_FILTERS,
    standardSetIds,
    curriculumProgramType,
  } = params;

  return {
    id,
    filters: {
      searchText,
      standardSetIds,
      ...standardFilters,
    },
    isPYPCurriculum: curriculumProgramType
      ? curriculumProgramType === CURRICULUM_TYPE_PYP
      : undefined,
  };
};

export const getStandardSetsQueryVariables = params => {
  const {
    assessmentId,
    searchText = "",
    curriculumProgramId,
    gradeScaleIds,
    selectedStandardSetDataScope,
    learningCourseId,
    assessmentSubjects = [],
    academicYearId,
    curriculumProgramType,
  } = params;

  const isSubjectBasedGrading =
    !getIsLearningCoursesEnabled() || getIsSubjectBasedGradingEnabled();

  const learningCourseVariables = getLearningCourseVariables({
    learningCourseId,
    academicYearId,
  });

  const learningCourse = getLearningCourseDetailsFromCache(
    learningCourseVariables
  );

  const learningCourseSubjectIds = _.map(
    _.get(learningCourse, "subjects"),
    "id"
  );

  const id =
    selectedStandardSetDataScope === DATA_SCOPE_ENUM.LEARNING_COURSE
      ? learningCourseId
      : curriculumProgramId;

  const type =
    selectedStandardSetDataScope === DATA_SCOPE_ENUM.LEARNING_COURSE
      ? "LEARNING_COURSE"
      : "CURRICULUM_PROGRAM";

  let subjectIds = [];
  if (isSubjectBasedGrading) {
    subjectIds = assessmentId ? assessmentSubjects : learningCourseSubjectIds;
  }

  const filters = {
    searchText:
      selectedStandardSetDataScope === DATA_SCOPE_ENUM.LEARNING_COURSE ||
      selectedStandardSetDataScope === DATA_SCOPE_ENUM.UNIT
        ? ""
        : searchText, // Don't pass searchText for course standard sets - use local search instead
    archivalState: "ACTIVE",
    gradeScaleIds,
    subjects: subjectIds,
    type: STANDARD_PLANNER_ELEMENT_TYPE_ENUM[curriculumProgramType],
  };

  return {
    id,
    type,
    ...getDatasetsOrderByArgs(),
    ...(type === "CURRICULUM_PROGRAM"
      ? { plannerElementNodeSetFilters: filters }
      : { plannerElementNodeFilters: filters }),
    isPYPCurriculum: curriculumProgramType
      ? curriculumProgramType === CURRICULUM_TYPE_PYP
      : undefined,
  };
};

export const getStandardEdges = params => {
  const { node, selectedStandardsDataScope } = params;

  if (selectedStandardsDataScope === DATA_SCOPE_ENUM.LEARNING_COURSE) {
    return _.flatMap(
      _.get(node, "standardsWrapper.standardsEdge") ||
        _.get(node, "benchmarksWrapper.standardsEdge"),
      edge => _.map(edge.standards || [], standard => ({ node: standard }))
    );
  } else {
    return (
      _.get(node, "standardSetWrapper.publishedStandards.edges") ||
      _.get(node, "standardSetWrapper.publishedBenchmarks.edges") ||
      []
    );
  }
};

export const getInitialSelectedStandardsForSet = params => {
  const { nodes, setId } = params;

  return _(nodes)
    .filter(node => _.get(node, "standardsWrapper.standardSet.id") === setId)
    .map("id")
    .compact()
    .value();
};

export const getDataSetScopeDropdownOptions = ({
  t,
  isUnitPlanTagged,
  unitName,
  isUnitScopeDisabled,
} = {}) => {
  const options = [];

  if (isUnitPlanTagged) {
    const unitOption = {
      key: DATA_SCOPE_ENUM.UNIT,
      label: t("classRoom:tagged_in_unit_name", { unitName }),
      value: DATA_SCOPE_ENUM.UNIT,
    };

    if (isUnitScopeDisabled) {
      unitOption.disabled = true;
      unitOption.disabledTooltip = t(
        "classRoom:no_standards_tagged_in_unit_name",
        { unitName }
      );
    }

    options.push(unitOption);
  }

  options.push(
    {
      key: DATA_SCOPE_ENUM.LEARNING_COURSE,
      label: t("classRoom:tagged_in_this_course"),
      value: DATA_SCOPE_ENUM.LEARNING_COURSE,
    },
    {
      key: DATA_SCOPE_ENUM.ALL,
      label: t("common:all"),
      value: DATA_SCOPE_ENUM.ALL,
    }
  );

  return options;
};
